import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import { toastSweetAlert } from '../../Helpers/ToastSweetAlert'
import { useLoading } from '../../../LoadingOverlay/LoadingOverlayContext'
import {
  COMPLETE_POINT_ROUTE_FROM_WATER_DRIVER,
  UPDATE_POINT_ROUTE_FROM_WATER_DRIVER,
} from '../../../graphql/Catalog/Trucks/Routes/routeWaterDriver'
import { fnObjNameStatus } from '../../../const/constStatuses_waterTruckRequest'

const textData = {
  start: {
    name: 'inicio',
    text: 'Traslado a estación y cargar pipa',
    btnTxt: 'Completar carga de pipa',
    icon: 'fas fa-tint',
  },
  loaded: {
    name: 'carga_pipa',
    text: 'Traslado a destino y entregar',
    btnTxt: 'Completar entrega',
    icon: 'fas fa-map-marker-alt',
  },
  delivered: {
    name: 'entrega_pipa',
    text: 'Entrega completada',
    btnTxt: 'Terminar',
    icon: 'fas fa-check',
  },
}

const statusesWaterTruck = fnObjNameStatus()

const WaterTruckRoutePoint = ({
  routePoint,
  refetch,
  setCurrentRoutePoint,
}) => {
  const {
    RoutePointsToWaterStation: station,
    RoutePointsToWaterTruckRequest: waterTruckRequest,
  } = routePoint
  const { showLoading, hideLoading } = useLoading()
  const refFileEvidence = useRef()

  //STATES
  const [file, setFile] = useState()
  const [previewFile, setPreviewFile] = useState(null)
  const [stateCurrent, setStateCurrent] = useState(textData.start)
  const [loadedComplete, setLoadedComplete] = useState(false)
  const [deliveredComplete, setDeliveredComplete] = useState(false)
  const [routeFinished, setRouteFinished] = useState(false)

  //MUTATIONS
  const [updatePoint] = useMutation(UPDATE_POINT_ROUTE_FROM_WATER_DRIVER)
  const [completePoint] = useMutation(COMPLETE_POINT_ROUTE_FROM_WATER_DRIVER)

  //FUNCTIONS
  const handleSetFileUpload = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0])
      putPreview(e)
    }
  }

  const putPreview = (e) => {
    if (e.target.files[0]) {
      const fileToLoad = e.target.files[0]
      const fileReader = new FileReader()
      let base64File
      fileReader.onload = function (event) {
        base64File = event.target.result
        setPreviewFile(base64File)
      }
      fileReader.readAsDataURL(fileToLoad)
    }
  }

  const handleCleanFile = () => {
    setFile(null)
    setPreviewFile(null)
    refFileEvidence.current.value = ''
  }

  const handleSubmit = async () => {
    if (routeFinished) return
    let input = {
      route_id: routePoint.route_id,
      route_point_id: routePoint.id,
      water_truck_request_id: routePoint.water_truck_request_id,
    }

    if (!loadedComplete || !deliveredComplete) {
      if (!file) {
        return toastSweetAlert({
          mode: 'error',
          message: 'Debes subir una evidencia',
        })
      }

      if (file) {
        const type = file.type.split('/')
        if (type[0] !== 'image' && ['jpg', 'jpeg', 'png'].includes(type[1])) {
          return toastSweetAlert({
            mode: 'error',
            message: 'Debe ser de tipo imagen',
          })
        }
        // if (file.size > 5242880) {
        //   return toastSweetAlert({
        //     mode: 'error',
        //     message: 'Archivo es demasiado pesado',
        //   })
        // }
        const maxSize = 20 * 1024 * 1024 // 20MB
        if (file.size > maxSize) {
          return toastSweetAlert({
            mode: 'error',
            message: `El archivo es demasiado pesado. Máximo permitido: ${(
              maxSize /
              (1024 * 1024)
            ).toFixed(2)} MB`,
          })
        }
      }
    }

    let finish = false

    const confirm = await toastSweetAlert({
      title:
        loadedComplete && deliveredComplete
          ? 'Concluir ruta'
          : 'Enviar evidencia',
      mode: 'modalConfirmCancel',
      confirmButtonText: 'Enviar',
      message: (
        <>
          {loadedComplete && deliveredComplete ? (
            <label className="d-block mb-3">
              Marcar como "{stateCurrent.text}". ¿Estás seguro de terminar la
              ruta?
            </label>
          ) : (
            <div className="row justify-content-center text-center">
              <div className="col-12">
                <label className="d-block mb-3">
                  Marcar como "{stateCurrent.text}". ¿Estás seguro de subir esta
                  imagen?
                </label>
                <img
                  src={URL.createObjectURL(file)}
                  alt="Vista previa de la imagen"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    maxHeight: '300px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                />
              </div>
            </div>
          )}
        </>
      ),
    })

    if (!confirm.isConfirmed) return
    showLoading()

    try {
      if (!loadedComplete || !deliveredComplete) {
        input.file = file
        await updatePoint({ variables: { input } })
      } else {
        await completePoint({ variables: { input } })
        setStateCurrent(textData.start)
        setLoadedComplete(false)
        setDeliveredComplete(false)
        setRouteFinished(false)
        finish = true
      }
      handleCleanFile()
      return await refetch()
    } catch (e) {
      return toastSweetAlert({
        mode: 'error',
        message: e.message,
      })
    } finally {
      hideLoading()
      if (finish) {
        return await toastSweetAlert(
          {
            mode: 'ok',
            message: 'Punto completado',
          },
          setCurrentRoutePoint(null)
        )
      }
    }
  }

  //EFFECTS
  useEffect(() => {
    setRouteFinished(false)
    setLoadedComplete(false)
    setDeliveredComplete(false)
    setStateCurrent(textData.start)
    setPreviewFile(null)
    setFile(null)
    const inputEvidence = document.getElementById('evidenceUpload')
    if (inputEvidence) inputEvidence.value = ''

    if (routePoint.loaded_at !== null) {
      setLoadedComplete(true)
    }
    if (routePoint.delivered_at !== null) {
      setDeliveredComplete(true)
    }

    if (routePoint.loaded_at === null && routePoint.delivered_at === null) {
      setStateCurrent(textData.start)
    }
    if (routePoint.loaded_at !== null && routePoint.delivered_at === null) {
      setStateCurrent(textData.loaded)
    }
    if (routePoint.loaded_at !== null && routePoint.delivered_at !== null) {
      setStateCurrent(textData.delivered)
    }

    if (
      routePoint.loaded_at !== null &&
      routePoint.delivered_at !== null &&
      routePoint.RoutePointsToWaterTruckRequest.status_id ===
        statusesWaterTruck.completado
    ) {
      setLoadedComplete(true)
      setDeliveredComplete(true)
      setRouteFinished(true)
    }
  }, [routePoint])

  return (
    <>
      <div className="col-12 col-lg-6 mb-1">
        <div className="card shadow-sm">
          <div className="card-header px-2 py-1">
            <h5 className="card-title">
              <i className="fas fa-tint mr-1" />
              <strong>Estación de agua</strong>
            </h5>
          </div>
          <div className="card-body px-3 py-2">
            <div>
              <strong>{station?.name}</strong>
            </div>
            <div style={{ fontSize: '0.9rem' }}>
              <i>Direccion: {station?.address}</i>
            </div>
            <div
              className="mt-2"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '1.5rem',
                color: '#007bff',
                cursor: 'pointer',
              }}
            >
              <a
                href={`https://www.google.com/maps?q=${station.location}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#007bff' }}
              >
                <i class="fab fa-google"></i>
              </a>

              <a
                href={`https://waze.com/ul?ll=${station.location}&navigate=yes`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#007bff' }}
              >
                <i class="fab fa-waze"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="col-12 col-lg-6 mb-1">
        <div className="card shadow-sm">
          <div className="card-header px-2 py-1">
            <h5 className="card-title">
              <i className="fas fa-map-marker-alt mr-1" />
              <strong>Destino de entrega</strong>
            </h5>
          </div>
          <div className="card-body px-3 py-2">
            <div className="">
              {waterTruckRequest?.AddressOfWaterTruckRequest.address_full}
              <div
                className="mt-2"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '1.5rem',
                  color: '#007bff',
                  cursor: 'pointer',
                }}
              >
                <a
                  href={`https://www.google.com/maps?q=${waterTruckRequest?.AddressOfWaterTruckRequest.latitude},${waterTruckRequest?.AddressOfWaterTruckRequest.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#007bff' }}
                >
                  <i class="fab fa-google"></i>
                </a>

                <a
                  href={`https://waze.com/ul?ll=${waterTruckRequest?.AddressOfWaterTruckRequest.latitude},${waterTruckRequest?.AddressOfWaterTruckRequest.longitude}&navigate=yes`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#007bff' }}
                >
                  <i class="fab fa-waze"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-12 mb-1">
        <div className="card shadow-sm pb-2">
          <div className="card-header px-2 py-1">
            <h5 className="card-title">
              <i className="fas fa-route mr-1" />
              <strong>Pasos a seguir</strong>
            </h5>
          </div>
          <div className="card-body">
            <span style={{ overflowWrap: 'break-word' }}>
              <strong>{stateCurrent.text}</strong>
            </span>
            <ol className="dash-progress-bar-status-tracking">
              <li className={loadedComplete ? 'is-complete' : ''}>
                <i className={`${textData.start.icon} fa-2x`} />
              </li>
              <li className={deliveredComplete ? 'is-complete' : ''}>
                <i className={`${textData.loaded.icon} fa-2x`} />
              </li>
              <li className={routeFinished ? 'is-complete' : ''}>
                <i className={`${textData.delivered.icon} fa-2x`} />
              </li>
            </ol>
          </div>
        </div>
      </div>

      {(!loadedComplete || !deliveredComplete) && (
        <div className="col-12 col-lg-6 mb-1">
          <div className="card shadow-sm">
            <div className="card-header px-2 py-1">
              <h5 className="card-title">
                <i className="fas fa-image mr-1" />
                <strong>Subir evidencia</strong>
              </h5>
            </div>
            <div className="card-body px-3 pt-3 pb-2">
              <input
                type="file"
                id="evidenceUpload"
                className="form-control mb-3"
                name="document"
                onChange={(e) => handleSetFileUpload(e)}
                ref={refFileEvidence}
                accept=".jpg, .jpeg, .png"
              />
              {file && (
                <div className="preview-docs">
                  <div>
                    <span
                      style={{
                        cursor: 'pointer',
                        fontSize: '2rem',
                      }}
                      className="icon-delete-document"
                      onClick={handleCleanFile}
                    >
                      <i className="fas fa-trash"></i>
                    </span>
                  </div>
                  <div>
                    <img
                      src={previewFile}
                      style={{ width: '100%' }}
                      alt="previewUploadImg"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!routeFinished && (
        <div className="col-12 mb-3 mt-2 text-right">
          <button
            type="button"
            className="btn btn-accept-login btn-lg"
            style={{ fontSize: '1.15rem' }}
            onClick={() => handleSubmit()}
          >
            {stateCurrent.btnTxt} <i className={`${stateCurrent.icon} ml-1 `} />
          </button>
        </div>
      )}

      <br />
    </>
  )
}

export default WaterTruckRoutePoint
